/*
 * Books' request and answer shapes (draft 10, FEATURE-MAP 10). Money is whole cents; a transaction's amount is plus for
 * money in and minus for money out of the account.
 */
import { z } from "zod";
import { CATEGORY_TYPES } from "./books.js";
import { ACCOUNT_KINDS } from "./books-review.js";

const day = z.iso.date();
const cents = z.number().int().min(-1_000_000_000).max(1_000_000_000);

export const categoryRow = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  type: z.enum([...CATEGORY_TYPES, "open"]),
  group: z.string(),
  taxLine: z.string().nullable(),
  parentId: z.uuid().nullable(),
  hidden: z.boolean(),
});
export type CategoryRow = z.infer<typeof categoryRow>;

/**
 * A month end check: what the bank's balance says the account held at the end of endDay (today's bank balance less every
 * bank line since), next to what Books says. ok means they matched and the month was reconciled by the Steward.
 */
export const reconcileCheck = z.object({
  endDay: z.string(),
  bankCents: z.number(),
  booksCents: z.number(),
  ok: z.boolean(),
  at: z.string(),
});
export type ReconcileCheck = z.infer<typeof reconcileCheck>;

export const bankAccountRow = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(ACCOUNT_KINDS),
  /** The owner's own account, not the business's: only business costs paid from it come into the books. */
  personal: z.boolean(),
  last4: z.string().nullable(),
  openingBalanceCents: z.number(),
  openingDay: z.string().nullable(),
  /** From a bank linked through Plaid. */
  linked: z.boolean(),
  /** The statement end day it is reconciled through, if ever. */
  reconciledThrough: z.string().nullable(),
  /** What the bank says it holds (owed is minus), and when it said so. Only for a linked bank. */
  bankBalanceCents: z.number().nullable(),
  bankBalanceAt: z.string().nullable(),
  /** What Books says it holds today: the opening balance and every line since. null with no opening balance yet. */
  booksBalanceCents: z.number().nullable(),
  /**
   * Like QuickBooks' Posted (owner, Nov 2: "as it should match"): the opening balance and only the lines a person or the
   * AI posted. Matches the bank once every line is posted. null with no opening balance yet.
   */
  postedBalanceCents: z.number().nullable().default(null),
  /** Lines waiting to be posted. */
  pending: z.number(),
  /** When the bank last sent lines, for a linked account. */
  lastSyncAt: z.string().nullable(),
  /** The Steward's last month end check against the bank (books/auto-reconcile.ts), for a linked account. */
  check: reconcileCheck.nullable().default(null),
});
export type BankAccountRow = z.infer<typeof bankAccountRow>;

export const ruleRow = z.object({
  id: z.uuid(),
  match: z.string(),
  categoryId: z.uuid(),
  payee: z.string().nullable(),
  /** in: money in only. out: money out only. null: either. */
  direction: z.enum(["in", "out"]).nullable(),
  /** One bank, card or cash account only, or null for all of them. */
  accountId: z.uuid().nullable(),
});
export type RuleRow = z.infer<typeof ruleRow>;

export const connectionRow = z.object({
  id: z.uuid(),
  institution: z.string().nullable(),
  status: z.string(),
  error: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
});

export const booksSetup = z.object({
  accounts: z.array(bankAccountRow),
  categories: z.array(categoryRow),
  rules: z.array(ruleRow),
  connections: z.array(connectionRow),
  /** Head office has saved Plaid's keys, so a bank can be linked. */
  plaidReady: z.boolean(),
  years: z.array(z.number()),
  /** The books are closed through this day: nothing on it or before changes until it is opened again. */
  lockDay: z.string().nullable(),
});
export type BooksSetup = z.infer<typeof booksSetup>;

export const accountInput = z.object({
  name: z.string().trim().min(1).max(60),
  kind: z.enum(ACCOUNT_KINDS),
  personal: z.boolean().optional(),
  last4: z
    .string()
    .regex(/^\d{4}$/)
    .nullable()
    .optional(),
  openingBalanceCents: cents.default(0),
  openingDay: day.nullable().optional(),
});

export const splitPart = z.object({ categoryId: z.uuid(), amountCents: z.number().int() });

export const transactionRow = z.object({
  id: z.uuid(),
  accountId: z.uuid(),
  day: z.string(),
  amountCents: z.number(),
  description: z.string(),
  categoryId: z.uuid().nullable(),
  auto: z.boolean(),
  split: z.array(splitPart).nullable(),
  payee: z.string().nullable(),
  note: z.string().nullable(),
  source: z.string(),
  matched: z.boolean(),
  reconciled: z.boolean(),
  importId: z.uuid().nullable(),
  /** Receipts and papers kept with it. */
  receipts: z.number(),
  /** Posted: a person looked at it (or it came in from the old books). Pending until then. */
  reviewed: z.boolean(),
  /** Left out of the books by a person, kept for the record. */
  excluded: z.boolean(),
  /** The other side of a transfer between our own accounts. */
  pairId: z.uuid().nullable(),
  /** Payments in the Steward this deposit brought to the bank (cash and checks). */
  matchedPayments: z.array(z.uuid()),
  /** For pending lines: how many lines could be the other side of a transfer, and payments it could be. */
  pairsFound: z.number(),
  paymentsFound: z.number(),
  /**
   * The AI reviewer's note: why it picked the category, if it is sure, personal or transfer, a tax note (meals half off,
   * a big buy to write off), and posted when the AI posted it by itself. Kept on a posted line only when the AI posted it.
   */
  ai: z
    .object({
      why: z.string(),
      sure: z.boolean(),
      hint: z.enum(["personal", "transfer"]).nullable(),
      tax: z.string().nullable().default(null),
      posted: z.boolean().default(false),
    })
    .nullable()
    .default(null),
});
export type TransactionRow = z.infer<typeof transactionRow>;

export const aiReviewBody = z.object({
  /** One account, or every business account. */
  accountId: z.uuid().optional(),
  /** Look again at lines it already reviewed. */
  again: z.boolean().optional(),
});
export const aiReviewAnswer = z.object({
  reviewed: z.number(),
  sure: z.number(),
  left: z.number(),
  /** Posted by the AI by itself (books.aiPosts on), and transfers it paired. */
  posted: z.number().default(0),
  paired: z.number().default(0),
});
export type AiReviewAnswer = z.infer<typeof aiReviewAnswer>;

/** One way to pay less tax, from the AI's tax review. */
export const taxMove = z.object({
  title: z.string(),
  why: z.string(),
  /** A rough dollar range it could save, in words, like "$800 to $1,500". Empty when it can't say. */
  saves: z.string(),
  /** What to do, in plain steps. */
  how: z.string(),
  /** By when, like "Before Dec 31" or "When you file". */
  when: z.string(),
  /** Big enough or tricky enough that a tax pro should sign off first. */
  askPro: z.boolean(),
});
export const taxReview = z.object({
  id: z.uuid(),
  year: z.number(),
  at: z.string(),
  /** Two or three plain lines on where the year stands. */
  summary: z.string(),
  moves: z.array(taxMove),
  /** Lines in the books that look wrong for taxes (personal costs, a big tool to write off, meals). */
  fixes: z.array(z.object({ what: z.string(), why: z.string() })),
  /** Dates to keep (quarterly payments, filing day, state returns). */
  dates: z.array(z.object({ day: z.string(), what: z.string() })),
  /** Web pages it read for this year's rules. */
  sources: z.array(z.object({ title: z.string(), url: z.string() })),
});
export type TaxReview = z.infer<typeof taxReview>;
export const taxAnswer = z.object({ review: taxReview.nullable() });
export type TaxAnswer = z.infer<typeof taxAnswer>;
export const taxReviewBody = z.object({ year: z.number().int().min(2000).max(2100).optional() });

export const transactionsQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  accountId: z.uuid().optional(),
  uncategorized: z.enum(["1"]).optional(),
  /** pending (not posted yet), posted, or excluded. None is every line in the books. */
  state: z.enum(["pending", "posted", "excluded"]).optional(),
  /** A span instead of a year. */
  from: day.optional(),
  to: day.optional(),
  categoryId: z.uuid().optional(),
});

export const typedTransaction = z.object({
  accountId: z.uuid(),
  day,
  amountCents: cents.refine((v) => v !== 0, "Type an amount."),
  description: z.string().trim().min(1).max(200),
  categoryId: z.uuid().nullable().optional(),
  note: z.string().trim().max(200).optional(),
});

export const transactionPatch = z.object({
  categoryId: z.uuid().nullable().optional(),
  note: z.string().trim().max(200).optional(),
  payee: z.string().trim().max(60).optional(),
  /** Two or more categories adding up to the amount (a store run that was part supplies, part office). null makes it one again. */
  split: z.array(splitPart).min(2).max(20).nullable().optional(),
});

export const bulkCategory = z.object({
  ids: z.array(z.uuid()).min(1).max(2000),
  categoryId: z.uuid().nullable(),
  /** Who was paid, on every one of them (a 1099 payee). Empty takes the payee off; left out leaves each as it is. */
  payee: z.string().trim().max(80).nullable().optional(),
});

/** Post lines as they are (a rule's or last time's category), leave them out of the books, or bring them back. */
export const bulkReview = z.object({ ids: z.array(z.uuid()).min(1).max(2000) });
export const bulkReviewAnswer = z.object({ changed: z.number(), skipped: z.number() });

/** What a pending line could be: the other side of a transfer, or payments in the Steward. */
export const matchesAnswer = z.object({
  pairs: z.array(
    z.object({
      id: z.uuid(),
      accountId: z.uuid(),
      accountName: z.string(),
      day: z.string(),
      amountCents: z.number(),
      description: z.string(),
      /** The category this side gets. */
      categoryName: z.string(),
    }),
  ),
  payments: z.array(
    z.object({
      /** One payment, or several on one day that add up to the deposit. */
      ids: z.array(z.uuid()),
      day: z.string(),
      amountCents: z.number(),
      label: z.string(),
    }),
  ),
  /** Stripe is set up, so a deposit can be looked up as a Stripe payout. */
  stripe: z.boolean(),
});
export type MatchesAnswer = z.infer<typeof matchesAnswer>;
export const pairInput = z.object({ otherId: z.uuid() });
export const matchPaymentsInput = z.object({ paymentIds: z.array(z.uuid()).min(1).max(50) });

export const importInput = z.object({
  accountId: z.uuid(),
  kind: z.enum(["csv", "ofx"]),
  /** The file's text. */
  text: z.string().min(1).max(5_000_000),
  /** The card's file shows charges as plus amounts: turn them to money out. */
  flip: z.boolean().default(false),
});
export const importAnswer = z.object({
  importId: z.uuid().nullable(),
  added: z.number(),
  skipped: z.number(),
  bad: z.number(),
  ruled: z.number(),
  /** Rows on or before the day the books are closed through, left out. */
  locked: z.number(),
});

export const ruleInput = z.object({
  match: z.string().trim().min(3, "Type at least 3 letters the bank shows, like SHELL or HOME DEPOT.").max(60),
  categoryId: z.uuid(),
  payee: z.string().trim().max(60).optional(),
  direction: z.enum(["in", "out"]).nullable().optional(),
  accountId: z.uuid().nullable().optional(),
});

export const categoryInput = z.object({
  name: z.string().trim().min(1).max(40),
  type: z.enum(CATEGORY_TYPES).default("expense"),
  group: z.string().max(20).optional(),
  parentId: z.uuid().optional(),
});

export const reconcileLook = z.object({ accountId: z.uuid(), endDay: day });
export const reconcileStart = z.object({
  beginCents: z.number(),
  from: z.string(),
  first: z.boolean(),
  transactions: z.array(transactionRow),
});
export const reconcileFinish = reconcileLook.extend({
  endBalanceCents: cents,
  ids: z.array(z.uuid()).max(5000),
});

export const adjustmentInput = z.object({
  day,
  memo: z.string().trim().min(1).max(200),
  lines: z.array(splitPart).min(2).max(20),
});
export const adjustmentRow = z.object({
  id: z.uuid(),
  day: z.string(),
  memo: z.string(),
  lines: z.array(splitPart),
  undone: z.boolean(),
});

export const pnlRow = z.object({
  categoryId: z.uuid(),
  name: z.string(),
  type: z.string(),
  group: z.string(),
  taxLine: z.string().nullable(),
  /** Income and money back to clients count as plus income; costs as plus costs. */
  byMonth: z.array(z.number()),
  totalCents: z.number(),
});
export const pnlAnswer = z.object({
  from: z.string(),
  to: z.string(),
  months: z.array(z.string()),
  rows: z.array(pnlRow),
  incomeCents: z.number(),
  directCents: z.number(),
  grossCents: z.number(),
  expenseCents: z.number(),
  profitCents: z.number(),
  boughtToKeepCents: z.number(),
  /** Money in or out with no category yet: it is not in profit until it has one. */
  uncategorizedCents: z.number(),
  uncategorizedCount: z.number(),
});
export type PnlAnswer = z.infer<typeof pnlAnswer>;

/**
 * Cash flow (owner, 2026-11-01: "thats whats more important to me"): money that went in and out of the business's
 * checking, savings and cash, by what it was for, month by month. Card buys count when the card is paid, not when made.
 */
export const cashFlowRow = z.object({
  /** null for lines with no category yet. */
  categoryId: z.uuid().nullable(),
  name: z.string(),
  /** Plus is money in, minus is money out. */
  byMonth: z.array(z.number()),
  totalCents: z.number(),
});
export const CASH_FLOW_SECTIONS = ["in", "out", "kept", "owner"] as const;
export const cashFlowSection = z.object({
  key: z.enum(CASH_FLOW_SECTIONS),
  rows: z.array(cashFlowRow),
  byMonth: z.array(z.number()),
  totalCents: z.number(),
});
export const cashFlowAnswer = z.object({
  from: z.string(),
  to: z.string(),
  months: z.array(z.string()),
  sections: z.array(cashFlowSection),
  netByMonth: z.array(z.number()),
  netCents: z.number(),
  /** Checking, savings and cash at the start and the end of each month. null when an account has no opening balance. */
  startByMonth: z.array(z.number().nullable()),
  endByMonth: z.array(z.number().nullable()),
  startCents: z.number().nullable(),
  endCents: z.number().nullable(),
});
export type CashFlowAnswer = z.infer<typeof cashFlowAnswer>;

export const balanceAnswer = z.object({
  day: z.string(),
  accounts: z.array(
    z.object({ id: z.uuid(), name: z.string(), kind: z.string(), personal: z.boolean(), balanceCents: z.number().nullable() }),
  ),
  cashCents: z.number(),
  cardsOwedCents: z.number(),
  boughtToKeepCents: z.number(),
  /** Loans received less the principal paid back, through the day. */
  loansOwedCents: z.number(),
  /** Since the books began: what the owner put in, and took out (draws). */
  ownerInCents: z.number(),
  ownerOutCents: z.number(),
  /** What the business is worth on these books: money and things kept, less what the cards and loans owe. */
  equityCents: z.number(),
});
export type BalanceAnswer = z.infer<typeof balanceAnswer>;

export const payee1099 = z.object({
  key: z.string(),
  name: z.string(),
  totalCents: z.number(),
  count: z.number(),
  reaches: z.boolean(),
  w9: z.boolean(),
  personId: z.uuid().nullable(),
  personName: z.string().nullable(),
});
export const answer1099 = z.object({ year: z.number(), lineCents: z.number(), payees: z.array(payee1099) });
export type Answer1099 = z.infer<typeof answer1099>;
export const payeeInput = z.object({ w9: z.boolean().optional(), personId: z.uuid().nullable().optional() });

export const brandPnlRow = z.object({
  companyId: z.uuid(),
  name: z.string(),
  incomeCents: z.number(),
  expenseCents: z.number(),
  profitCents: z.number(),
});

/** One line of the general ledger: a category moved by a transaction or a journal entry. */
export const ledgerLine = z.object({
  id: z.uuid(),
  day: z.string(),
  accountId: z.uuid(),
  /** Plus is a debit. Income shows as minus here, as in any ledger. */
  amountCents: z.number(),
  /** bank_tx (a transaction) or adjust (a journal entry). */
  sourceType: z.string(),
  sourceId: z.uuid(),
  /** The bank's words, or the journal entry's memo. */
  description: z.string(),
  payee: z.string().nullable(),
  /** The bank, card or cash account it went through, for a transaction. */
  bankAccountId: z.uuid().nullable(),
});
export type LedgerLine = z.infer<typeof ledgerLine>;
export const ledgerQuery = z.object({ from: day, to: day, categoryId: z.uuid().optional() });

export const reconcileRow = z.object({
  id: z.uuid(),
  accountId: z.uuid(),
  endDay: z.string(),
  beginCents: z.number(),
  endCents: z.number(),
  count: z.number(),
  undone: z.boolean(),
  /** Only an account's latest reconcile can be undone. */
  latest: z.boolean(),
  /** The Steward did it by itself: the month matched the bank to the cent. */
  auto: z.boolean().default(false),
  createdAt: z.string(),
});
export type ReconcileRow = z.infer<typeof reconcileRow>;

export const lockInput = z.object({ day: day.nullable() });
